import { useQuery } from '@tanstack/react-query';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { accountsApi } from '../api';
import {
  Typography, Box, CircularProgress, Alert, Grid, Paper,
  Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Button, Link
} from '@mui/material';
import { Add as AddIcon, Visibility as ViewIcon } from '@mui/icons-material';
import AIInsightsFeed from '../components/AIInsightsFeed';

// Define the detailed types to match our backend update
interface AIInsight {
  id: string;
  generated_by_agent: string;
  insight_text: string;
  created_at: string;
}

interface Contact {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  insights?: AIInsight[];
}

interface Deal {
  id: string;
  name: string;
  amount: number;
  stage: string;
  insights?: AIInsight[];
}

interface AccountDetails {
  id: string;
  name: string;
  domain: string;
  contacts: Contact[];
  deals: Deal[];
}

const fetchAccountDetails = async (id: string): Promise<AccountDetails> => {
  return await accountsApi.getAccount(id);
};

export function AccountDetailPage() {
  const { accountId } = useParams<{ accountId: string }>();

  const { data: account, isLoading, isError } = useQuery<AccountDetails>({
    queryKey: ['account', accountId],
    queryFn: () => fetchAccountDetails(accountId!),
    enabled: !!accountId, // Only run the query if accountId exists
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (isError) return <Alert severity="error">Error fetching account details.</Alert>;
  if (!account) return <Alert severity="info">No account found.</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h3" gutterBottom>{account.name}</Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {account.domain}
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={8}>
          {/* Deals Table */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5">Deals ({account.deals.length})</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                component={RouterLink}
                to={`/deals/new?account_id=${account.id}`}
                size="small"
              >
                Add Deal
              </Button>
            </Box>

            {account.deals.length > 0 ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Stage</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {account.deals.map((deal) => (
                    <TableRow key={deal.id}>
                      <TableCell>
                        <Link component={RouterLink} to={`/deals/${deal.id}`}>
                          {deal.name}
                        </Link>
                      </TableCell>
                      <TableCell>${deal.amount.toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={deal.stage}
                          size="small"
                          color={
                            deal.stage === 'Closed Won' ? 'success' :
                              deal.stage === 'Closed Lost' ? 'error' :
                                deal.stage === 'Negotiation' ? 'warning' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          component={RouterLink}
                          to={`/deals/${deal.id}`}
                          startIcon={<ViewIcon />}
                          size="small"
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography color="text.secondary">No deals found for this account.</Typography>
            )}
          </Paper>

          {/* Contacts Table */}
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5">Contacts ({account.contacts.length})</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                component={RouterLink}
                to={`/contacts/new?account_id=${account.id}`}
                size="small"
              >
                Add Contact
              </Button>
            </Box>

            {account.contacts.length > 0 ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {account.contacts.map((contact) => (
                    <TableRow key={contact.id}>
                      <TableCell>
                        <Link component={RouterLink} to={`/contacts/${contact.id}`}>
                          {contact.first_name && contact.last_name
                            ? `${contact.first_name} ${contact.last_name}`
                            : contact.email
                          }
                        </Link>
                      </TableCell>
                      <TableCell>{contact.email}</TableCell>
                      <TableCell>{contact.title || '-'}</TableCell>
                      <TableCell>
                        <Button
                          component={RouterLink}
                          to={`/contacts/${contact.id}`}
                          startIcon={<ViewIcon />}
                          size="small"
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography color="text.secondary">No contacts found for this account.</Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <AIInsightsFeed deals={account.deals} contacts={account.contacts} />
        </Grid>
      </Grid>
    </Box>
  );
} 